import type {
  Product,
  Vehicle,
  VehicleManufacturer,
  VehicleModel,
  VehicleType,
  Category,
  PaginatedResponse,
  RegistrationResult,
  VinLookupResult,
  Order,
  OrderAddress,
  AuthResponse,
  CustomerProfile,
  GarageItem,
  Customer,
  WishlistItem,
  Notification,
  RecoverOrderResponse,
  Passform,
  KortPassform,
} from '@/types';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem('autofrance-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.state?.token || null;
      }
    } catch {}
    return null;
  }

  private getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const stored = localStorage.getItem('autofrance-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.state?.refreshToken || null;
      }
    } catch {}
    return null;
  }

  private async tryRefreshToken(): Promise<boolean> {
    // Mutex: if a refresh is already in progress, wait for it
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        const refreshToken = this.getRefreshToken();
        if (!refreshToken) return false;

        const res = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (!res.ok) return false;

        const data = await res.json();
        if (!data.token || !data.refresh_token) return false;

        // Update store with new tokens
        const { useAuthStore } = await import('@/stores/auth');
        useAuthStore.getState().setTokens(data.token, data.refresh_token);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async fetch<T>(
    endpoint: string,
    options?: RequestInit,
    _isRetry = false
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    // Auto-refresh on 401 (only on client-side, only once)
    if (response.status === 401 && !_isRetry && typeof window !== 'undefined') {
      const refreshed = await this.tryRefreshToken();
      if (refreshed) {
        return this.fetch<T>(endpoint, options, true);
      }
      // Refresh failed — logout
      const { useAuthStore } = await import('@/stores/auth');
      if (useAuthStore.getState().token) {
        useAuthStore.getState().logout();
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: 'Unknown error',
        message: response.statusText,
      }));
      throw new Error(error.message || 'API request failed');
    }

    return response.json();
  }

  // Auth
  async login(email: string, password: string): Promise<AuthResponse> {
    return this.fetch('/customers/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async googleLogin(credential: string): Promise<AuthResponse> {
    return this.fetch('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });
  }

  async register(data: {
    email: string;
    password: string;
    firstname: string;
    lastname: string;
    newsletter?: boolean;
  }): Promise<AuthResponse> {
    return this.fetch('/customers/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async forgotPassword(email: string): Promise<{ success: boolean; message: string }> {
    return this.fetch('/customers/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<{ success: boolean }> {
    return this.fetch('/customers/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
    return this.fetch('/customers/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async getProfile(): Promise<CustomerProfile> {
    return this.fetch('/customers/me');
  }

  async updateProfile(data: Partial<Pick<Customer, 'firstname' | 'lastname' | 'phone' | 'company'>>): Promise<{ customer: Customer }> {
    return this.fetch('/customers/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getGarageItems(): Promise<GarageItem[]> {
    return this.fetch('/customers/me/garage');
  }

  async addGarageItem(data: { ktype: number; registration?: string; nickname?: string }): Promise<GarageItem> {
    return this.fetch('/customers/me/garage', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async removeGarageItem(id: number): Promise<{ success: boolean }> {
    return this.fetch(`/customers/me/garage/${id}`, {
      method: 'DELETE',
    });
  }

  // Wishlist
  async getWishlist(): Promise<WishlistItem[]> {
    return this.fetch('/customers/me/wishlist');
  }

  async getWishlistIds(): Promise<number[]> {
    return this.fetch('/customers/me/wishlist/ids');
  }

  async addToWishlist(productId: number): Promise<{ success: boolean; product_id: number }> {
    return this.fetch('/customers/me/wishlist', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
  }

  async removeFromWishlist(productId: number): Promise<{ success: boolean }> {
    return this.fetch(`/customers/me/wishlist/${productId}`, {
      method: 'DELETE',
    });
  }

  // Push notifications
  async getVapidKey(): Promise<{ publicKey: string }> {
    return this.fetch('/customers/vapid-key');
  }

  async subscribePush(subscription: PushSubscriptionJSON): Promise<{ success: boolean }> {
    return this.fetch('/customers/me/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  async unsubscribePush(endpoint?: string): Promise<{ success: boolean }> {
    return this.fetch('/customers/me/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  async getPushStatus(): Promise<{ subscribed: boolean }> {
    return this.fetch('/customers/me/push/status');
  }

  // Notifications
  async getNotifications(): Promise<{ notifications: Notification[]; unread_count: number }> {
    return this.fetch('/customers/me/notifications');
  }

  async markNotificationsRead(id?: number): Promise<{ success: boolean }> {
    return this.fetch('/customers/me/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
  }

  // Products
  async getProducts(params?: {
    page?: number;
    limit?: number;
    category?: number;
    manufacturer?: number;
    search?: string;
    ktype?: number;
  }): Promise<PaginatedResponse<Product>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.category) searchParams.set('category', String(params.category));
    if (params?.manufacturer) searchParams.set('manufacturer', String(params.manufacturer));
    if (params?.search) searchParams.set('q', params.search);
    if (params?.ktype) searchParams.set('ktype', String(params.ktype));

    const query = searchParams.toString();
    return this.fetch(`/products${query ? `?${query}` : ''}`);
  }

  async getProduct(id: number): Promise<Product> {
    return this.fetch(`/products/${id}`);
  }

  async searchProducts(query: string): Promise<Product[]> {
    return this.fetch(`/products/search?q=${encodeURIComponent(query)}`);
  }

  async getProductAlternatives(id: number, ktype?: number): Promise<{
    alternatives: Array<{
      id: number;
      reference: string;
      name: string;
      price: number;
      manufacturer_name: string;
      quality: string;
      image_url: string | null;
    }>;
    current_quality: string;
  }> {
    const params = ktype ? `?ktype=${ktype}` : '';
    return this.fetch(`/products/${id}/alternatives${params}`);
  }

  // Vehicles
  async getManufacturers(): Promise<VehicleManufacturer[]> {
    return this.fetch('/vehicles/manufacturers');
  }

  async getModels(manufacturer: string): Promise<VehicleModel[]> {
    return this.fetch(`/vehicles/models?manufacturer=${encodeURIComponent(manufacturer)}`);
  }

  async getVehicleTypes(manufacturer: string, model: string): Promise<VehicleType[]> {
    return this.fetch(
      `/vehicles/types?manufacturer=${encodeURIComponent(manufacturer)}&model=${encodeURIComponent(model)}`
    );
  }

  async getVehicle(ktype: number): Promise<Vehicle> {
    return this.fetch(`/vehicles/${ktype}`);
  }

  async getVehicleProducts(
    ktype: number,
    params?: { page?: number; limit?: number; category?: number }
  ): Promise<PaginatedResponse<Product>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.category) searchParams.set('category', String(params.category));

    const query = searchParams.toString();
    return this.fetch(`/vehicles/${ktype}/products${query ? `?${query}` : ''}`);
  }

  // Registration lookup
  async lookupRegistration(plate: string, source?: string): Promise<RegistrationResult> {
    const s = source ? `&source=${encodeURIComponent(source)}` : '';
    return this.fetch(`/regnum/lookup?plate=${encodeURIComponent(plate)}${s}`);
  }

  async lookupVin(vin: string): Promise<VinLookupResult> {
    return this.fetch(`/regnum/vin?vin=${encodeURIComponent(vin)}`);
  }

  /** Capture an anonymous "save my car" lead (email opt-in for remarketing). */
  async saveVehicleLead(lead: {
    email: string; ktype?: number | null; plate?: string | null;
    vehicle_name?: string; manufacturer?: string; model?: string;
    consent: boolean; source?: string;
  }): Promise<{ ok: boolean }> {
    return this.fetch('/regnum/lead', { method: 'POST', body: JSON.stringify(lead) });
  }

  // Categories
  async getCategories(): Promise<Category[]> {
    return this.fetch('/categories');
  }

  async getCategoryProducts(
    categoryId: number,
    params?: { page?: number; limit?: number; ktype?: number }
  ): Promise<PaginatedResponse<Product>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.ktype) searchParams.set('ktype', String(params.ktype));

    const query = searchParams.toString();
    return this.fetch(`/categories/${categoryId}/products${query ? `?${query}` : ''}`);
  }

  // Checkout
  /**
   * Exakt passform ur TecDoc (via Triscan): motor, tillverkningsår och övriga villkor för
   * just den här bilen, och vilka delar i samma kategori som bevisligen passar.
   */
  async passform(productId: number, ktype: number, plate?: string | null): Promise<Passform> {
    const q = new URLSearchParams({ ktype: String(ktype) });
    if (plate) q.set('plate', plate);
    return this.fetch(`/passform/${productId}?${q}`);
  }

  /** Passform ur TecDoc för en hel resultatsida — nyckel är produkt-id. */
  async passformLista(ids: number[], ktype: number, plate?: string | null): Promise<{ passform: Record<number, KortPassform> }> {
    return this.fetch('/passform/lista', { method: 'POST', body: JSON.stringify({ ids, ktype, plate: plate ?? undefined }) });
  }

  async validateVehicles(items: Array<{ product_id: number; ktype: number }>): Promise<{
    /**
     * `kalla` säger HUR vi vet: 'direkt' står i tillverkarens passformsdata,
     * 'oe' är vår slutledning från ett delat originalnummer. Två tredjedelar av
     * alla besked kommer ur slutledningen (mätt 2026-09-24), och den kan ha
     * fel — därför ska ett grönt "Passar din bil" bara ges för 'direkt'.
     */
    results: Array<{ product_id: number; ktype: number; compatible: boolean; kalla?: 'direkt' | 'oe' | null }>;
  }> {
    return this.fetch('/checkout/validate-vehicles', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async createOrder(data: {
    items: Array<{ product_id: number; quantity: number; vehicle_ktype?: number; registration_plate?: string; vehicle_match_status?: string }>;
    shipping_address: OrderAddress;
    billing_address?: OrderAddress;
    vehicle_ktype?: number;
    country?: string;
    currency?: 'SEK' | 'EUR';
    customer_type?: string;
    review_credit_amount?: number;
    campaign_code?: string;
    consent_level?: string;
    marketing_consent?: boolean;
    gclid?: string;
    gbraid?: string;
    msclkid?: string;
    wbraid?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    referrer?: string;
    landing_page?: string;
    landing_at?: string;
    first_seen_at?: string;
    page_views?: number;
    time_on_site_ms?: number;
    idempotency_key?: string;
    forberedd_kod?: string;
    /** Kassalänk från en partnersajt: vem kunden kom ifrån och partnerns egen ordernyckel. */
    partner_id?: number;
    partner_ref?: string;
  // 'verkstad' = skickas till partnerverkstaden kunden kom ifrån. Samma frakt
  // som standard; valet sparas för att både vi och partnern ska se vart paketet gick.
  shipping_method?: 'standard' | 'pickup' | 'verkstad';
  }): Promise<Order> {
    return this.fetch('/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async initiateBamboraPayment(orderId: number, consentLevel?: string): Promise<{
    token: string;
    url: string;
  }> {
    return this.fetch('/checkout/bambora', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, ...(consentLevel && { consent_level: consentLevel }) }),
    });
  }

  /** Best-effort payment-funnel logging: report a non-success outcome (user cancelled at
   * Bambora, or returned to the confirmation page without a completed payment). */
  async reportPaymentOutcome(
    orderId: number,
    outcome: 'cancelled' | 'returned_unpaid' | 'init_failed'
  ): Promise<void> {
    try {
      await this.fetch('/checkout/payment-outcome', {
        method: 'POST',
        body: JSON.stringify({ orderId, outcome }),
      });
    } catch { /* best-effort, ignore */ }
  }

  async initiateInvoicePayment(
    orderId: number,
    orgNumber: string,
    invoiceEmail: string,
    invoicePhone: string
  ): Promise<{ success: boolean }> {
    return this.fetch('/checkout/invoice', {
      method: 'POST',
      body: JSON.stringify({
        order_id: orderId,
        org_number: orgNumber,
        invoice_email: invoiceEmail,
        invoice_phone: invoicePhone,
      }),
    });
  }

  async getCheckoutSettings(): Promise<{
    invoice_payment_days: number;
    payment_bambora_enabled: boolean;
    payment_invoice_enabled: boolean;
  }> {
    return this.fetch('/checkout/settings');
  }

  async getShippingInfo(country?: string): Promise<{
    tiers: Array<{ min: number; max: number | null; price: number }>;
    currency: string;
    delivery_days: string;
    international_message: string;
  }> {
    const params = country ? `?country=${country}` : '';
    return this.fetch(`/checkout/shipping${params}`);
  }

  async recoverOrder(reference: string): Promise<RecoverOrderResponse> {
    return this.fetch(`/checkout/orders/recover/${encodeURIComponent(reference)}`);
  }

  async getOrder(id: number, ref: string): Promise<Order> {
    // ref är obligatorisk: API:et svarar 404 utan den, så att ett gissat
    // löpnummer inte räcker för att läsa en annan kunds beställning.
    return this.fetch(`/checkout/orders/${id}?ref=${encodeURIComponent(ref)}`);
  }

  async lookupOrder(reference: string, email: string): Promise<Order> {
    return this.fetch('/checkout/orders/lookup', {
      method: 'POST',
      body: JSON.stringify({ reference, email }),
    });
  }

  // Part Requests
  async createPartRequest(formData: FormData): Promise<{ id: number; ticket_number: string; status: string }> {
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}/part-requests`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Okänt fel' }));
      throw new Error(error.message || 'Kunde inte skapa förfrågan');
    }
    return res.json();
  }

  async getMyPartRequests(page = 1): Promise<{ data: unknown[]; total: number; page: number; total_pages: number }> {
    return this.fetch(`/part-requests/my?page=${page}`);
  }

  async getMyPartRequest(id: number): Promise<unknown> {
    return this.fetch(`/part-requests/my/${id}`);
  }

  async sendPartRequestMessage(id: number, formData: FormData): Promise<unknown> {
    const token = this.getToken();
    const res = await fetch(`${this.baseUrl}/part-requests/my/${id}/message`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    });
    if (!res.ok) {
      const error = await res.json().catch(() => ({ message: 'Okänt fel' }));
      throw new Error(error.message || 'Kunde inte skicka meddelande');
    }
    return res.json();
  }

  async acceptPartRequest(id: number): Promise<{ order_id: number; reference: string; payment_method: string; payment_url?: string }> {
    return this.fetch(`/part-requests/my/${id}/accept`, { method: 'POST' });
  }

  async declinePartRequest(id: number, reason?: string): Promise<{ success: boolean }> {
    return this.fetch(`/part-requests/my/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async expeditePartRequest(id: number): Promise<{ success: boolean }> {
    return this.fetch(`/part-requests/my/${id}/expedite`, { method: 'POST' });
  }

  async cancelPartRequest(id: number): Promise<{ success: boolean }> {
    return this.fetch(`/part-requests/my/${id}/cancel`, { method: 'POST' });
  }

  async getPartRequestUnreadCount(): Promise<{ count: number }> {
    return this.fetch('/part-requests/my/unread-count');
  }

  // Product Reviews
  async getProductReviews(productId: number, page = 1): Promise<{
    data: Array<{
      id: number; rating: number; text: string | null;
      verified: boolean; created_at: string; author: string;
    }>;
    total: number; page: number; total_pages: number;
  }> {
    return this.fetch(`/products/${productId}/reviews?page=${page}`);
  }

  async submitProductReview(productId: number, rating: number, text?: string, token?: string): Promise<{
    id: number; rating: number; text: string | null; verified: boolean; created_at: string;
  }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    else {
      const t = this.getToken();
      if (t) headers['Authorization'] = `Bearer ${t}`;
    }
    const res = await fetch(`${this.baseUrl}/products/${productId}/reviews`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ rating, text }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: 'Okänt fel' }));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async updateProductReview(productId: number, rating?: number, text?: string): Promise<{
    id: number; rating: number; text: string | null; verified: boolean;
  }> {
    return this.fetch(`/products/${productId}/reviews`, {
      method: 'PUT',
      body: JSON.stringify({ rating, text }),
    });
  }

  async deleteProductReview(productId: number): Promise<{ success: boolean }> {
    return this.fetch(`/products/${productId}/reviews`, { method: 'DELETE' });
  }

  async getReviewCredits(): Promise<ReviewCreditProgress> {
    return this.fetch('/customers/me/review-credits');
  }

  // Campaigns
  async getCampaignProducts(params?: {
    ktype?: number; page?: number; limit?: number;
  }): Promise<CampaignProductsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.ktype) searchParams.set('ktype', String(params.ktype));
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return this.fetch(`/campaigns/products${query ? `?${query}` : ''}`);
  }

  async getActiveCampaigns(): Promise<{ campaigns: CampaignPublic[] }> {
    return this.fetch('/campaigns/active');
  }

  // ─── Car Listings (Partner Bilar till salu) ─────────────────────────────
  async getCarListings(params?: {
    search?: string; make?: string; fuel?: string; transmission?: string;
    min_price?: number; max_price?: number; min_year?: number; max_year?: number;
    max_mileage?: number; sort?: string;
  }): Promise<CarListingsResponse> {
    const sp = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '' && v !== null) sp.set(k, String(v));
      }
    }
    const qs = sp.toString();
    return this.fetch(`/cars/listings${qs ? `?${qs}` : ''}`);
  }

  async getCarListing(id: number): Promise<CarListingDetail> {
    return this.fetch(`/cars/listings/${id}`);
  }

  async placeCarBid(listingId: number, data: {
    bid_amount?: number; message?: string; bid_type?: string;
    contact_name?: string; contact_email?: string; contact_phone?: string;
    contact_address?: string; company_name?: string; org_number?: string;
  }): Promise<{ success: boolean; lead: { id: number; bid_amount: number | null; created_at: string; status: string } }> {
    return this.fetch(`/cars/listings/${listingId}/bid`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export interface ReviewCreditProgress {
  review_count: number;
  balance: number;
  total_earned: number;
  total_used: number;
  next_milestone: {
    review_count: number;
    credit_amount: number;
    reviews_remaining: number;
  } | null;
  history: Array<{
    id: number;
    amount: number;
    balance_after: number;
    type: string;
    description: string | null;
    created_at: string;
  }>;
}

export interface CampaignPublic {
  id: number;
  name: string;
  discount_type: string;
  discount_value: number;
  scope: string;
  ends_at: string;
  description: string | null;
  category_ids?: number[];
}

export interface CampaignProductsResponse {
  data: import('@/types').Product[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  campaigns: CampaignPublic[];
}

export interface CarListingItem {
  id: number;
  heading: string;
  subtitle: string | null;
  model_spec: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  mileage: number | null;
  mileage_unit: string | null;
  price_amount: number | null;
  price_currency: string | null;
  fuel: string | null;
  transmission: string | null;
  body_type: string | null;
  exterior_color: string | null;
  engine_effect: string | null;
  image_url: string | null;
  all_images: { uri: string }[] | null;
  location: string | null;
  partner: { id: number; name: string } | null;
  first_seen_at: string;
  total_bids: number;
  my_bids: { id: number; bid_amount: number | null; message?: string | null; created_at: string; status: string }[];
  has_bid: boolean;
}

export interface CarListingsResponse {
  listings: CarListingItem[];
  filters: { makes: string[]; fuels: string[] };
}

export interface CarListingDetail extends CarListingItem {
  wheel_drive: string | null;
  engine_volume: number | null;
  no_of_seats: number | null;
  first_registration: string | null;
  max_trailer_weight: string | null;
  service_history: string | null;
  service_history_info: string | null;
  warranty_info: string | null;
  equipment: { category?: string; value: string }[] | null;
  chassis_number: string | null;
  regno: string | null;
  description: string | null;
  status: string;
  highest_bid: { amount: number; created_at: string; bid_type: string } | null;
}

export const api = new ApiClient(API_BASE);
